/**
 * @file RepLineBlock.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description One document line as a block on a rep's phone: its code and
 *              name, its words, then its figures in labelled cells. Cam,
 *              28 Oct 2026 (rep mobile review, "All 20"): the invoice, PO
 *              and order windows put their lines in a seven- to nine-column
 *              table that ran off the side of the phone ("QUAI", "DISC",
 *              the PO number broken over two lines). On the phone each line
 *              is a block instead, the same block the Sales Orders card
 *              opens (RepOrderCard), so a line reads the same everywhere.
 *
 * `facts` is [{ key, label, value, none, align }]: `value` is any node (a
 * QtyWithTonnes, a price…); `none` is said in muted text when there is no
 * value ("Not yet", "Not priced"). Three to a row; four are two by two.
 * `children` goes underneath — the order card's PO button, a transport line.
 */

import React from 'react';
import './RepOrderCard.css';

export function RepLineBlocks({ empty = 'No lines', children }) {
  const has = React.Children.toArray(children).length > 0;
  return (
    <div className="rep-line-blocks">
      {has ? children : <div className="roc-lines-empty">{empty}</div>}
    </div>
  );
}

function RepLineBlock({ code, name, words = [], facts = [], children }) {
  const shownWords = (Array.isArray(words) ? words : [words])
    .map(w => (w == null ? '' : String(w).trim()))
    .filter(w => w && w !== name);
  const cols = facts.length === 4 ? 2 : Math.max(1, Math.min(3, facts.length));
  return (
    <div className="roc-line">
      <div className="roc-line-head">
        {code && <span className="roc-code">{code}</span>}
        <span className="roc-line-name">{name || code || 'Line'}</span>
      </div>
      {shownWords.map((w, i) => <div key={i} className="roc-line-words">{w}</div>)}
      {facts.length > 0 && (
        <div className="roc-line-facts" style={{ '--roc-cols': cols }}>
          {facts.map((f, i) => (
            <div
              key={f.key || f.label || i}
              className={`roc-fact${f.align === 'right' ? ' roc-fact-price' : ''}`}
            >
              <span className="roc-fact-label">{f.label}</span>
              {f.value != null && f.value !== '' && f.value !== false
                ? <span className="roc-fact-value-wrap">{f.value}</span>
                : <span className="roc-fact-none">{f.none || '—'}</span>}
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

export default RepLineBlock;
